import {ComponentType, useState} from 'react'
import {Redirect, useHistory, useLocation, useParams} from 'react-router-dom'
import Button from 'jbc-front/components/Button'
import {useMutation, useQuery} from 'hooks/graphql'
import {LoadingPage} from 'components/ui/LoadingPage'
import {CommentModal} from 'components/modals/CommentModal'
import {useSelector} from 'hooks/redux'
import {useNotify} from 'hooks/useNotify'
import {ProcedureContext, ReportFormProps} from 'reportForms/ReportForm'
import {AttachmentsForm} from '../AttachmentsForm'
import {ChildcareMethodSelect} from './ChildcareMethodSelect'
import {ChildcareInfoForm} from './ChildcareInfoForm'
import {
  PROCEDURE_FLOW_STATUS,
  ProcedureFlowStatusQueryResult,
  ProcedureFlowStatusQueryVariables,
  REJECT_PROCEDURE_STATUS,
  RejectProcedureStatusQueryVariables,
  UPDATE_PROCEDURE_FLOW_STATUS,
} from './query'
import {FlowIndex, FlowMode, FlowStatus, FormCode} from './types'
import {decamelizeComments} from './decamelizeComments'
import {StartProgressAndRedirect} from './finish/StartProgressAndRedirect'
import {Apply} from './finish/Apply'

// @ts-ignore
import {formMap as reportFormMap} from 'procedureStatuses/ReportEdit'
// @ts-ignore
import ReviewComments from 'components/ReviewComments'
// @ts-ignore
import NotFound from 'NotFound'

const formMap: Record<FormCode, ComponentType<ReportFormProps>> = {
  ...reportFormMap,
  Attachments: AttachmentsForm,
  ChildcareMethodSelect: ChildcareMethodSelect,
  ChildcareInfo: ChildcareInfoForm,
}

const DEFAULT_FLOW_STATUS: FlowStatus = Object.freeze({
  detailFinished: false,
  status: {} as Record<FlowIndex, boolean>,
})
const makeFlowStatus = (flowStatus: Partial<FlowStatus> | undefined): FlowStatus =>
  flowStatus
    ? {...DEFAULT_FLOW_STATUS, ...flowStatus, status: {...DEFAULT_FLOW_STATUS.status, ...flowStatus.status}}
    : DEFAULT_FLOW_STATUS

const decideFlowMode = (
  procedureContext: ProcedureContext,
  procedureType: string,
  status: string
): FlowMode | undefined => {
  if (procedureContext === 'admin' && status === 'draft') {
    return 'draft'
  }
  if (
    [
      'start_childcare',
      'extend_childcare',
      'finish_childcare',
      'parental_leave',
      'parental_leave_support_allowance',
    ].includes(procedureType)
  ) {
    if (procedureContext === 'admin' && status === 'applying') {
      return 'confirm'
    }
    if (procedureContext === 'mypage' && ['employee_draft', 'request'].includes(status)) {
      return 'draft'
    }
  }
  return undefined
}

const needDetailInputProcedure = (procedureType: string) => ['enroll', 'resign'].includes(procedureType)

const procedureTmpTypeFromContext = (procedureContext: ProcedureContext) =>
  procedureContext === 'admin' ? 'draft' : 'employee_draft'

export type FlowGenerator<FlowQueryData> = (
  data: FlowQueryData,
  flowStatus: FlowStatus,
  flowMode: FlowMode
) => Generator<FlowIndex>

export interface FlowQueryContext {
  procedureStatusId: string
}

export type UseFlowQuery<FlowQueryData> = (context: FlowQueryContext) => {
  loading: boolean
  data?: FlowQueryData
  refetch: () => void
}

interface FlowProps<FlowQueryData> {
  /** 手続き画面の呼び出し文脈 */
  procedureContext: ProcedureContext
  /** 参照中の手続きで、入力が必要な帳票・フォーム ({@link FlowIndex}) を列挙するジェネレータを指定 */
  makeFlow: FlowGenerator<FlowQueryData>
  /** makeFlowで参照するデータを読み込むためのフックを指定 */
  useFlowQuery: UseFlowQuery<FlowQueryData>
}

/**
 * 手続きの進捗状況に基づいて、入力が必要な帳票を描画するコンポーネント
 */
export const Flow = <FlowQueryData,>({procedureContext, makeFlow, useFlowQuery}: FlowProps<FlowQueryData>) => {
  const {id: procedureStatusId} = useParams<{id: string}>()
  const location = useLocation()
  const history = useHistory()
  const notify = useNotify()
  const [isOpenCommentModal, setIsOpenCommentModal] = useState(false)
  const currentUser = useSelector((state) => state.session.currentUser)

  const {data, loading, refetch} = useQuery<ProcedureFlowStatusQueryResult, ProcedureFlowStatusQueryVariables>(
    PROCEDURE_FLOW_STATUS,
    {
      variables: {
        procedureStatusId,
        typeForFlowStatus: procedureTmpTypeFromContext(procedureContext),
      },
      fetchPolicy: 'network-only',
    }
  )
  const procedureStatus = data?.client?.procedureStatus
  const flowStatus = makeFlowStatus(procedureStatus?.flowStatus?.data)

  const flowQueryResult = useFlowQuery({procedureStatusId})

  const [updateProcedureFlowStatus] = useMutation(UPDATE_PROCEDURE_FLOW_STATUS)
  const updateFlowStatus = async (flowIndex: FlowIndex, value: boolean, nextFlowIndex: FlowIndex | undefined) => {
    const {data} = await refetch()
    if (!data.client.procedureStatus) {
      throw new Error('procedure status not found')
    }
    const freshFlowStatus = makeFlowStatus(data.client.procedureStatus.flowStatus?.data)
    const newFlowStatus: FlowStatus = {
      ...freshFlowStatus,
      status: {...freshFlowStatus.status, [flowIndex]: value},
    }
    await updateProcedureFlowStatus({
      variables: {
        input: {
          procedureStatusId,
          type: procedureTmpTypeFromContext(procedureContext),
          name: 'flow_status',
          data: newFlowStatus,
        },
        typeForFlowStatus: procedureTmpTypeFromContext(procedureContext),
      },
    })
    flowQueryResult.refetch()

    history.push({pathname: location.pathname, hash: nextFlowIndex ? `#${nextFlowIndex}` : undefined})
    window.scrollTo(0, 0)
  }

  const [rejectProcedureStatus] = useMutation<unknown, RejectProcedureStatusQueryVariables>(REJECT_PROCEDURE_STATUS)

  if (loading || flowQueryResult.loading) {
    return <LoadingPage />
  }
  if (!procedureStatus) {
    return <NotFound />
  }
  if (!flowQueryResult.data) {
    return null // データ取得エラー
  }

  const flowMode = decideFlowMode(procedureContext, procedureStatus.procedureType, procedureStatus.status)
  if (!flowMode) {
    return <Redirect to={`/procedure_statuses/${procedureStatusId}`} />
  }
  if (needDetailInputProcedure(procedureStatus.procedureType) && !flowStatus.detailFinished) {
    return <Redirect to={`/${procedureStatus.procedureType}/detail/${procedureStatusId}`} />
  }
  if (procedureContext === 'mypage' && procedureStatus.employee.user?.id !== String(currentUser.id)) {
    return <NotFound />
  }

  const flow = [...makeFlow(flowQueryResult.data, flowStatus, flowMode)]
  // 全帳票入力済チェック
  if (flow.every((report) => flowStatus.status[report])) {
    switch (procedureContext) {
      case 'admin':
        return <StartProgressAndRedirect flowMode={flowMode} procedureStatusId={procedureStatusId} />
      case 'mypage':
        return (
          <Apply
            procedureStatusId={procedureStatusId}
            procedureType={procedureStatus.procedureType}
            comments={procedureStatus.procedureComments}
            onCancel={async () => {
              const lastIndex = flow.at(-1)!
              await updateFlowStatus(lastIndex, false, lastIndex)
            }}
          />
        )
      default:
        return procedureContext satisfies never // exhaustiveness check
    }
  }

  const current = location.hash ? (location.hash.slice(1) as FlowIndex) : ''
  if (!current || !flow.includes(current)) {
    const notFinished = flow.find((report) => !flowStatus.status[report])
    if (notFinished) {
      return <Redirect to={{pathname: location.pathname, hash: `#${notFinished}`}} />
    } else {
      return null // 全帳票入力済の場合、先にStartProgressAndRedirectを返しているはずなので、ここには来ない
    }
  }
  const currentIndex = flow.indexOf(current)
  const nextIndex = flow.findIndex(
    (report, index) => (index < currentIndex && !flowStatus.status[report]) || index > currentIndex
  )
  const [report, dependentId] = current.split('_') as [FormCode, string | undefined]
  const Form = formMap[report]

  const submitText = (() => {
    if (nextIndex >= 0) {
      if (flowMode === 'confirm') return '承認して次へ'
      else return '次へ'
    }
    if (procedureContext === 'mypage') return '申請する'
    return '完了'
  })()
  const comments = decamelizeComments(procedureStatus.procedureComments)

  const afterSubmit = async () => {
    await updateFlowStatus(current, true, nextIndex >= 0 ? flow[nextIndex] : undefined)
  }

  const handleReject = async (comment: string) => {
    await rejectProcedureStatus({
      variables: {
        input: {
          procedureStatusId,
          comment,
        },
      },
    })
    notify('依頼しました', 'success')
    history.push('/dashboard')
  }

  return (
    <div>
      {Form && (
        <Form
          key={dependentId}
          submitText={submitText}
          procedureStatusId={procedureStatusId}
          afterSubmit={afterSubmit}
          dependentId={dependentId}
          procedureContext={procedureContext}
          isConfirm={flowMode === 'confirm'}
          comments={(flowMode === 'confirm' || procedureContext === 'mypage') && <ReviewComments comments={comments} />}
          renderReject={
            flowMode === 'confirm'
              ? ({isSubmitting}) => (
                  <Button className="u-mr20" disabled={isSubmitting} onClick={() => setIsOpenCommentModal(true)}>
                    修正を依頼する
                  </Button>
                )
              : undefined
          }
        />
      )}
      <CommentModal
        title="修正依頼"
        isOpen={isOpenCommentModal}
        onSubmit={({comment}) => handleReject(comment)}
        onClose={() => setIsOpenCommentModal(false)}
        comments={comments}
      />
      <FlowDebugger
        flow={flow}
        flowMode={flowMode}
        flowStatus={flowStatus}
        procedureContext={procedureContext}
        currentIndex={currentIndex}
        nextIndex={nextIndex}
      />
    </div>
  )
}

const FlowDebugger =
  process.env['NODE_ENV'] === 'development'
    ? ({
        flow,
        flowMode,
        flowStatus,
        procedureContext,
        currentIndex,
        nextIndex,
      }: {
        flow: FlowIndex[]
        flowMode: FlowMode
        flowStatus: FlowStatus
        procedureContext: ProcedureContext
        currentIndex: number
        nextIndex: number
      }) => {
        return (
          <div
            style={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              zIndex: 100,
              background: '#444',
              color: '#fff',
              pointerEvents: 'none',
            }}
          >
            <div style={{padding: '.5rem', background: '#222'}}>Flow</div>
            <div style={{padding: '.5rem', borderBottom: '1px solid #222'}}>Context: {procedureContext}</div>
            <div style={{padding: '.5rem', borderBottom: '1px solid #222'}}>Mode: {flowMode}</div>
            <ul style={{padding: '.5rem'}}>
              {flow.map((report, index) => (
                <li key={report} style={{textDecoration: index === currentIndex ? 'underline' : ''}}>
                  {index === currentIndex && '→ '}
                  {flowStatus.status[report] && '✓ '}
                  {report}
                  {index === nextIndex && ' (next)'}
                </li>
              ))}
            </ul>
          </div>
        )
      }
    : () => null
